import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Calendar, Clock, RefreshCw, AlertCircle, ChevronLeft, ChevronRight, ChevronDown, Globe, Loader2 } from 'lucide-react';
import { meetingService } from '../../services/meetingService';
import { calendarService } from '../../services/calendarService';
import { useProfile } from '../../contexts/ProfileContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTimezone } from '../../hooks/useTimezone';
import { formatInTz } from '../../utils/dateFormatters';
import type { AvailabilitySettings, TimeSlot } from '../../types/calendar';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
  'Europe/Berlin', 'Europe/Madrid', 'Europe/Rome', 'Europe/Amsterdam',
  'Europe/Brussels', 'Europe/Vienna', 'Europe/Zurich', 'Europe/Stockholm',
  'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Helsinki', 'Europe/Dublin',
  'Europe/Warsaw', 'Europe/Prague', 'Europe/Budapest', 'Europe/Athens',
  'Asia/Dubai', 'Asia/Riyadh', 'Asia/Kuwait', 'Asia/Bahrain', 'Asia/Muscat',
  'Asia/Kolkata', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Singapore', 'Asia/Seoul',
  'Asia/Hong_Kong', 'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Manila',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland', 'UTC',
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const DAY_INDEX_TO_NAME: Record<number, string> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

function formatTimezoneLabel(tz: string): string {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(now);
    const tzAbbr = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const city = tz.split('/').pop()?.replace(/_/g, ' ') || tz;
    return `${city} (${tzAbbr})`;
  } catch {
    return tz;
  }
}

function formatSlotTime(isoString: string, timezone: string): { value: string; label: string } {
  const date = new Date(isoString);
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const label = formatter.format(date);

  const hFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const value = hFormatter.format(date).replace(/^24:/, '00:');

  return { value, label };
}

interface RescheduleMeetingModalProps {
  meeting: any;
  onClose: () => void;
  onSuccess: () => void;
}

interface CustomDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  disabledDays?: Set<number>;
  isLoadingSettings?: boolean;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({ value, onChange, minDate, disabledDays, isLoadingSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value + 'T12:00:00');
    return new Date();
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 8, left: rect.left, width: Math.max(rect.width, 300) });
    }
    setIsOpen(!isOpen);
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDateObj = minDate ? new Date(minDate + 'T00:00:00') : today;

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const selectedParts = value ? value.split('-').map(Number) : [];

  const handlePrev = () => setViewDate(new Date(year, month - 1, 1));
  const handleNext = () => setViewDate(new Date(year, month + 1, 1));

  const handleDayClick = (day: number) => {
    const m = (month + 1).toString().padStart(2, '0');
    const d = day.toString().padStart(2, '0');
    onChange(`${year}-${m}-${d}`);
    setIsOpen(false);
  };

  const isDisabled = (day: number): boolean => {
    const date = new Date(year, month, day);
    if (date < minDateObj) return true;
    if (disabledDays && disabledDays.has(date.getDay())) return true;
    return false;
  };

  const isSelected = (day: number): boolean => {
    return selectedParts.length === 3 && selectedParts[0] === year && selectedParts[1] === month + 1 && selectedParts[2] === day;
  };

  const isToday = (day: number): boolean => {
    const t = new Date();
    return t.getFullYear() === year && t.getMonth() === month && t.getDate() === day;
  };

  const isNonWorkingDay = (day: number): boolean => {
    if (!disabledDays) return false;
    const date = new Date(year, month, day);
    return disabledDays.has(date.getDay());
  };

  const formatDisplay = () => {
    if (!value) return '';
    const [y, m, d] = value.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="w-full px-4 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm cursor-pointer transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 font-medium text-left flex items-center justify-between group"
      >
        <span className="flex items-center gap-2">
          <Calendar size={15} className="text-[#1875FF]" />
          <span className={value ? 'text-gray-900' : 'text-gray-400'}>
            {value ? formatDisplay() : 'Select date'}
          </span>
        </span>
        <div className="flex items-center gap-1.5">
          {isLoadingSettings && <Loader2 size={14} className="text-[#1875FF] animate-spin" />}
          <ChevronDown className={`w-4 h-4 text-[#1875FF] transition-all duration-300 group-hover:scale-110 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn p-4"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={handlePrev}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-gray-900">
              {MONTH_NAMES[month]} {year}
            </span>
            <button
              type="button"
              onClick={handleNext}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0 mb-0.5">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center text-[11px] font-semibold text-gray-400 uppercase tracking-wider py-0.5">
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0">
            {days.map((day, i) => {
              if (day === null) return <div key={`empty-${i}`} className="h-8" />;
              const disabled = isDisabled(day);
              const selected = isSelected(day);
              const todayMark = isToday(day);
              const nonWorking = isNonWorkingDay(day);

              return (
                <button
                  key={day}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleDayClick(day)}
                  title={nonWorking && !disabled ? '' : undefined}
                  className={`
                    w-full h-8 flex items-center justify-center text-sm rounded-lg transition-all font-medium
                    ${disabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover:bg-[#1875FF]/10 hover:text-[#1875FF]'}
                    ${disabled && nonWorking ? 'text-gray-300 bg-gray-50/50' : ''}
                    ${selected ? 'bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white shadow-md shadow-[#1875FF]/30 hover:text-white' : ''}
                    ${todayMark && !selected ? 'ring-2 ring-[#1875FF]/30 text-[#1875FF] font-bold' : ''}
                    ${!disabled && !selected && !todayMark ? 'text-gray-700' : ''}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface AvailabilityTimePickerProps {
  value: string;
  onChange: (time: string) => void;
  slots: TimeSlot[];
  isLoading: boolean;
  hasDate: boolean;
  timezone: string;
}

const AvailabilityTimePicker: React.FC<AvailabilityTimePickerProps> = ({ value, onChange, slots, isLoading, hasDate, timezone }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const formattedSlots = slots.map((slot) => {
    const { value: timeValue, label } = formatSlotTime(slot.start, timezone);
    return { value: timeValue, label, isoStart: slot.start };
  });

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    if (isOpen && scrollRef.current && value) {
      const idx = formattedSlots.findIndex((s) => s.value === value);
      if (idx >= 0) {
        const el = scrollRef.current.children[idx] as HTMLElement;
        if (el) el.scrollIntoView({ block: 'center' });
      }
    }
  }, [isOpen, value, formattedSlots]);

  const handleToggle = () => {
    if (!hasDate) return;
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
    }
    setIsOpen(!isOpen);
    setSearch('');
  };

  const selectedSlot = formattedSlots.find((s) => s.value === value);
  const filtered = search
    ? formattedSlots.filter((s) => s.label.toLowerCase().includes(search.toLowerCase()))
    : formattedSlots;

  const getPlaceholder = () => {
    if (!hasDate) return 'Select a date first';
    if (isLoading) return 'Loading available times...';
    return 'Select time';
  };

  return (
    <div ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={!hasDate || isLoading}
        className={`w-full px-4 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm transition-all font-medium text-left flex items-center justify-between group ${
          !hasDate || isLoading ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10'
        }`}
      >
        <span className="flex items-center gap-2">
          {isLoading ? (
            <Loader2 size={15} className="text-[#05E0D1] animate-spin" />
          ) : (
            <Clock size={15} className="text-[#05E0D1]" />
          )}
          <span className={selectedSlot ? 'text-gray-900' : 'text-gray-400'}>
            {selectedSlot ? selectedSlot.label : getPlaceholder()}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-[#1875FF] transition-all duration-300 group-hover:scale-110 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {formattedSlots.length > 6 && (
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search time..."
                autoFocus
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] outline-none transition-all placeholder:text-gray-400"
              />
            </div>
          )}
          <div ref={scrollRef} className="max-h-56 overflow-y-auto custom-scrollbar">
            {filtered.length > 0 ? (
              filtered.map((slot, i) => (
                <button
                  key={slot.value}
                  type="button"
                  onClick={() => {
                    onChange(slot.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full px-4 py-2.5 text-left hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium text-sm ${
                    slot.value === value ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'text-gray-900'
                  } ${i === 0 ? '' : 'border-t border-gray-100'}`}
                >
                  {slot.label}
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">
                {search ? 'No matching times' : 'No available times on this day'}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

interface CustomTimezonePickerProps {
  value: string;
  onChange: (tz: string) => void;
}

const CustomTimezonePicker: React.FC<CustomTimezonePickerProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const opensUp = spaceBelow < dropdownHeight;
      if (opensUp) {
        setDropdownPos({ top: rect.top - dropdownHeight - 8, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
      }
    }
    setIsOpen(!isOpen);
    setSearch('');
  };

  const options = TIMEZONES.map((tz) => ({ value: tz, label: formatTimezoneLabel(tz) }));
  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()) || o.value.toLowerCase().includes(search.toLowerCase()))
    : options;
  const selected = options.find((o) => o.value === value);

  return (
    <div ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="w-full px-4 py-2.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm cursor-pointer transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 font-medium text-left flex items-center justify-between group"
      >
        <span className="flex items-center gap-2 truncate">
          <Globe size={15} className="text-[#1875FF] flex-shrink-0" />
          <span className={selected ? 'text-gray-900 truncate' : 'text-gray-400'}>
            {selected ? selected.label : 'Select timezone'}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-[#1875FF] transition-all duration-300 flex-shrink-0 group-hover:scale-110 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && dropdownPos && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search timezone..."
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] outline-none transition-all placeholder:text-gray-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto custom-scrollbar">
            {filtered.length > 0 ? (
              filtered.map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full px-4 py-2.5 text-left hover:bg-[#1875FF]/5 hover:text-[#1875FF] transition-colors font-medium text-sm ${
                    opt.value === value ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'text-gray-900'
                  } ${i === 0 ? '' : 'border-t border-gray-100'}`}
                >
                  {opt.label}
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">No matching timezones</div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const RescheduleMeetingModal: React.FC<RescheduleMeetingModalProps> = ({
  meeting,
  onClose,
  onSuccess,
}) => {
  const { profile } = useProfile();
  const { user } = useAuth();
  const userTz = useTimezone();
  const currentDate = meeting.meeting_date || '';
  const [newDate, setNewDate] = useState(currentDate);
  const [newTime, setNewTime] = useState('');
  const [timezone, setTimezone] = useState(
    meeting.timezone || profile?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formattedCurrentSchedule = useMemo(() => {
    const date = meeting.meeting_date;
    const time = meeting.meeting_time;
    if (!date) return { date: 'N/A', time: 'N/A' };
    try {
      const timePart = time ? time.substring(0, 5) : '00:00';
      const naiveIso = `${date}T${timePart}:00`;
      const meetingTz = meeting.timezone;

      if (meetingTz && meetingTz.trim()) {
        const utcGuess = new Date(naiveIso + 'Z');
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: meetingTz,
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });
        const parts = formatter.formatToParts(utcGuess);
        const get = (type: string) => parts.find(p => p.type === type)?.value || '0';
        const renderedInMeetingTz = new Date(
          `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}Z`
        );
        const offsetMs = renderedInMeetingTz.getTime() - utcGuess.getTime();
        const utcInstant = new Date(utcGuess.getTime() - offsetMs);

        return {
          date: formatInTz(utcInstant, 'MMM dd, yyyy', userTz),
          time: formatInTz(utcInstant, 'h:mm a', userTz),
        };
      }

      return {
        date: formatInTz(new Date(naiveIso + 'Z'), 'MMM dd, yyyy', 'UTC'),
        time: (() => {
          const hour = parseInt(timePart.split(':')[0], 10);
          const minute = timePart.split(':')[1];
          const h = hour % 12 || 12;
          const ampm = hour >= 12 ? 'PM' : 'AM';
          return `${h}:${minute} ${ampm}`;
        })(),
      };
    } catch {
      return { date: date, time: time || 'N/A' };
    }
  }, [meeting.meeting_date, meeting.meeting_time, meeting.timezone, userTz]);

  const [availabilitySettings, setAvailabilitySettings] = useState<AvailabilitySettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const clientId = meeting.client_id || user?.client_id || '';

  useEffect(() => {
    if (!clientId) {
      setIsLoadingSettings(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const settings = await calendarService.getAvailabilitySettings(clientId);
      if (!cancelled) {
        setAvailabilitySettings(settings);
        setIsLoadingSettings(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  const disabledDays = React.useMemo(() => {
    if (!availabilitySettings) return undefined;
    const disabled = new Set<number>();
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const dayName = DAY_INDEX_TO_NAME[dayIndex];
      const key = `is_day_enabled_${dayName}` as keyof AvailabilitySettings;
      if (!availabilitySettings[key]) {
        disabled.add(dayIndex);
      }
    }
    return disabled.size > 0 ? disabled : undefined;
  }, [availabilitySettings]);

  const fetchSlotsForDate = useCallback(async (dateStr: string) => {
    if (!clientId || !dateStr) return;

    setIsLoadingSlots(true);
    setSlotsError(null);
    setAvailableSlots([]);
    setNewTime('');

    try {
      const startDate = new Date(dateStr + 'T00:00:00Z').toISOString();
      const endDate = new Date(dateStr + 'T23:59:59Z').toISOString();

      const response = await calendarService.getCalendarAvailability({
        client_id: clientId,
        start_date: startDate,
        end_date: endDate,
        timezone,
        mode: 'reschedule',
        sales_member_id: meeting.sales_member_id,
      });

      if (response.success && response.slots) {
        setAvailableSlots(response.slots);
        if (response.slots.length === 0) {
          setSlotsError('No available times on this day. Try another date.');
        }
      } else {
        setSlotsError(response.error || 'Could not load available times.');
      }
    } catch {
      setSlotsError('Failed to check calendar availability.');
    } finally {
      setIsLoadingSlots(false);
    }
  }, [clientId, timezone]);

  const handleDateChange = useCallback((date: string) => {
    setNewDate(date);
    if (date) {
      fetchSlotsForDate(date);
    } else {
      setAvailableSlots([]);
      setNewTime('');
    }
  }, [fetchSlotsForDate]);

  useEffect(() => {
    if (newDate && clientId && !isLoadingSettings) {
      fetchSlotsForDate(newDate);
    }
  }, [timezone]);

  const handleReschedule = async () => {
    if (!newDate || !newTime) {
      setError('Please select both a new date and time.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const startTime = `${newDate}T${newTime}:00`;

      const result = await meetingService.rescheduleMeeting({
        client_id: meeting.client_id,
        user_id: meeting.user_id,
        lead_id: meeting.lead_id,
        start_time: startTime,
        timezone,
        topic: meeting.topic,
        sales_member_id: meeting.sales_member_id,
      });

      if (!result.success) {
        setError(result.error || 'Failed to reschedule meeting.');
        setIsSubmitting(false);
        return;
      }

      if (meeting.lead_id && meeting.client_id && meeting.user_id) {
        const meetingLink = result.meeting_link || meeting.meeting_link || '';

        let additionalEmails: string[] | undefined;
        if (meeting.attendees) {
          try {
            const parsed = typeof meeting.attendees === 'string'
              ? JSON.parse(meeting.attendees)
              : meeting.attendees;
            if (Array.isArray(parsed) && parsed.length > 0) {
              additionalEmails = parsed.filter((e: unknown) => typeof e === 'string' && e.length > 0);
            }
          } catch {
            // attendees field not parseable, fallback to server-side lookup
          }
        }

        await meetingService.prepareMeetingReminders({
          lead_id: meeting.lead_id,
          client_id: meeting.client_id,
          user_id: meeting.user_id,
          type: 'rescheduled',
          meeting_datetime: startTime,
          meeting_link: meetingLink,
          timezone: meeting.timezone || timezone,
          source: 'ui',
          additional_emails: additionalEmails,
          booked_agent: meeting.booked_channel || undefined,
        });
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred.');
      setIsSubmitting(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl shadow-black/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#05E0D1]/20 bg-gradient-to-r from-[#05E0D1]/5 to-[#1875FF]/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#05E0D1] to-[#1875FF] rounded-xl flex items-center justify-center shadow-lg">
              <RefreshCw className="text-white" size={18} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 font-display">Reschedule Meeting</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {meeting.company_name || 'Select a new date and time'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white rounded-lg"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Current Schedule</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Calendar size={14} className="text-[#1875FF]" />
                {formattedCurrentSchedule.date}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Clock size={14} className="text-[#05E0D1]" />
                {formattedCurrentSchedule.time}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Date</label>
            <CustomDatePicker
              value={newDate}
              onChange={handleDateChange}
              minDate={today}
              disabledDays={disabledDays}
              isLoadingSettings={isLoadingSettings}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Time</label>
            <AvailabilityTimePicker
              value={newTime}
              onChange={setNewTime}
              slots={availableSlots}
              isLoading={isLoadingSlots}
              hasDate={!!newDate}
              timezone={timezone}
            />
            {slotsError && !isLoadingSlots && (
              <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle size={12} />
                {slotsError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Timezone</label>
            <CustomTimezonePicker value={timezone} onChange={setTimezone} />
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={isSubmitting || !newDate || !newTime}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-xl hover:shadow-lg hover:shadow-[#1875FF]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Rescheduling...
              </>
            ) : (
              <>
                <RefreshCw size={16} />
                Reschedule
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RescheduleMeetingModal;
